import { ArrowRight, Sparkles, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import Sunburst from "@/components/decor/Sunburst";

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 lg:pt-44 pb-20 lg:pb-32"
    >
      <div className="absolute inset-0 bg-grid opacity-60 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div className="brand-glow absolute inset-0" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700 shadow-sm animate-fadeUp"
            >
              <Sparkles size={14} className="text-magenta" />
              Agenție de performance marketing
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span className="text-electric">București · România</span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02] text-slate-900 animate-fadeUp"
              style={{ animationDelay: "80ms" }}
            >
              Reclame care{" "}
              <span className="text-magenta">aduc clienți,</span>
              <br className="hidden sm:block" />
              nu doar{" "}
              <span className="text-electric">impresii.</span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed animate-fadeUp"
              style={{ animationDelay: "160ms" }}
            >
              Construim campanii de <strong className="text-slate-900">Google Ads</strong> și{" "}
              <strong className="text-slate-900">Social Media Marketing</strong> pentru branduri din
              România care vor creștere reală — cu tracking corect, creative
              care convertesc și raportare transparentă.
            </p>

            <div
              className="mt-8 flex flex-col sm:flex-row gap-3 animate-fadeUp"
              style={{ animationDelay: "240ms" }}
            >
              <Button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("contact")}
                className="rounded-full bg-magenta hover:bg-[#C2185B] text-white h-12 px-6 text-base font-semibold group"
              >
                Solicită o ofertă
                <ArrowRight
                  size={18}
                  className="ml-2 transition-transform group-hover:translate-x-1"
                />
              </Button>
              <Button
                data-testid="hero-cta-secondary"
                variant="outline"
                onClick={() => scrollTo("services")}
                className="rounded-full h-12 px-6 text-base font-semibold border-slate-300 bg-white hover:bg-white hover:text-magenta hover:border-magenta transition-colors"
              >
                Vezi serviciile
              </Button>
            </div>

            <div
              className="mt-10 flex flex-wrap items-center gap-6 text-sm text-slate-500 animate-fadeUp"
              style={{ animationDelay: "320ms" }}
            >
              <div
                data-testid="always-online-badge"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                Suntem mereu <span className="brand-gradient-text">ON-line</span>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-magenta font-semibold">Google Partner</span>
                <span>·</span>
                <span className="text-electric font-semibold">Meta Business Partner</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative animate-float">
              <div className="absolute -inset-6 bg-gradient-to-tr from-[#E91E8B]/25 via-transparent to-[#2962FF]/25 rounded-[2.5rem] blur-2xl" />

              {/* Blue card with sunburst — inspired by v04 PDF proposal */}
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10 aspect-square bg-gradient-to-br from-[#2962FF] via-[#1E40AF] to-[#0F1E4A]">
                {/* Soft inner glow */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.18),transparent_60%)]" />

                {/* Magenta accent radial */}
                <div className="absolute -top-12 -right-12 h-56 w-56 rounded-full bg-magenta/30 blur-3xl" />

                {/* Sunburst illustration */}
                <Sunburst
                  data-testid="hero-sunburst"
                  className="absolute inset-0 w-full h-full p-8 animate-slow-rotate"
                  color="#FFFFFF"
                  strokeWidth={1.5}
                />

                {/* Top-left label */}
                <div className="absolute top-6 left-6 inline-flex items-center gap-2 text-white/90 text-xs font-semibold uppercase tracking-wider">
                  <Wifi size={14} />
                  Always on-line
                </div>

                {/* Bottom-left tagline */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-white/60 text-xs font-medium uppercase tracking-[0.2em]">
                    Busy doing client work
                  </div>
                  <div className="mt-2 font-display text-white text-2xl sm:text-3xl font-extrabold leading-tight">
                    Performance
                    <br />
                    <span className="text-magenta">non-stop</span>.
                  </div>
                </div>
              </div>

              {/* Floating cards */}
              <div className="absolute -left-4 sm:-left-10 top-10 bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-900/10 p-4 w-56">
                <div className="text-xs text-slate-500 font-medium">ROAS lunar</div>
                <div className="font-display text-3xl font-extrabold text-slate-900 mt-1">
                  4.8<span className="text-magenta">x</span>
                </div>
                <div className="text-xs text-emerald-600 font-semibold mt-1">+38% vs luna trecută</div>
              </div>

              <div className="absolute -right-4 sm:-right-6 bottom-16 bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-900/10 p-4 w-52">
                <div className="text-xs text-slate-500 font-medium">CPA mediu</div>
                <div className="font-display text-3xl font-extrabold text-slate-900 mt-1">
                  -41<span className="text-electric">%</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">După restructurare PMax</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
